import React from 'react';
import styled from 'styled-components';
import type { HTMLAttributes } from 'react';
import type { AllColorType } from '../../util/types';
import type { IconProps } from './types';
import IconWrapper from './common/IconWrapper';
import { ICON_STUDENT_BACKGROUND, ICON_STUDENT_BORDER } from '../../util/data-id';

type Ref = HTMLDivElement;
type HTMLProps = Omit<HTMLAttributes<Ref>, 'color'>;
interface Props extends Omit<IconProps, 'color'>, HTMLProps {
  /**
   * Sets background color.
   */
  background?: AllColorType | null;
  /**
   * Sets stroke/border color.
   */
  borderColor?: AllColorType | null;
}

const StudentIcon = React.forwardRef<Ref, Props>(
  ({
    size,
    background = null,
    borderColor = 'primary',
    ...other
  }: Props, ref): JSX.Element => {
    return (
      <IconWrapper
        ref={ref}
        size={size}
        color={null}
        {...(other as HTMLProps)}
      >
        <StudentIconStyled
          viewBox={'0 0 24 24'}
          aria-hidden={'true'}
          focusable={'false'}
          fill={'currentColor'}
          xmlns={'http://www.w3.org/2000/svg'}
          $background={background}
          $borderColor={borderColor}
        >
          <mask id="path-1-inside-1" fill="white">
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M13.3361 13.8195C15.4488 13.2351 17 11.2987 17 9C17 6.23858 14.7614 4 12 4C9.23858 4 7 6.23858 7 9C7 11.3123 8.56962 13.258 10.7015 13.8297L7.75211 19.0049C7.37218 19.6715 7.8536 20.5 8.62092 20.5H15.3888C16.1539 20.5 16.6356 19.6757 16.26 19.0091L13.3361 13.8195Z"
            />
          </mask>
          <path
            id={other.id as string ?? null}
            data-id={ICON_STUDENT_BACKGROUND}
            fillRule="evenodd"
            clipRule="evenodd"
            d="M13.3361 13.8195C15.4488 13.2351 17 11.2987 17 9C17 6.23858 14.7614 4 12 4C9.23858 4 7 6.23858 7 9C7 11.3123 8.56962 13.258 10.7015 13.8297L7.75211 19.0049C7.37218 19.6715 7.8536 20.5 8.62092 20.5H15.3888C16.1539 20.5 16.6356 19.6757 16.26 19.0091L13.3361 13.8195Z"
          />
          <path
            id={other.id as string ?? null}
            data-id={ICON_STUDENT_BORDER}
            d="M13.3361 13.8195L13.2028 13.3376L12.5867 13.508L12.9005 14.0649L13.3361 13.8195ZM10.7015 13.8297L11.1359 14.0773L11.4566 13.5146L10.831 13.3468L10.7015 13.8297ZM7.75211 19.0049L8.18652 19.2524L7.75211 19.0049ZM16.26 19.0091L15.8244 19.2546L16.26 19.0091ZM16.5 9C16.5 11.0681 15.1045 12.8115 13.2028 13.3376L13.4694 14.3014C15.7932 13.6586 17.5 11.5293 17.5 9H16.5ZM12 4.5C14.4853 4.5 16.5 6.51472 16.5 9H17.5C17.5 5.96243 15.0376 3.5 12 3.5V4.5ZM7.5 9C7.5 6.51472 9.51472 4.5 12 4.5V3.5C8.96243 3.5 6.5 5.96243 6.5 9H7.5ZM10.831 13.3468C8.91215 12.8322 7.5 11.0804 7.5 9H6.5C6.5 11.5442 8.22709 13.6838 10.572 14.3127L10.831 13.3468ZM10.2671 13.5822L7.31771 18.7573L8.18652 19.2524L11.1359 14.0773L10.2671 13.5822ZM7.31771 18.7573C6.7478 19.7573 7.46995 21 8.62092 21V20C8.23726 20 7.99655 19.5858 8.18652 19.2524L7.31771 18.7573ZM8.62092 21H15.3888V20H8.62092V21ZM15.3888 21C16.5365 21 17.259 19.7636 16.6956 18.7637L15.8244 19.2546C16.0122 19.5879 15.7714 20 15.3888 20V21ZM16.6956 18.7637L13.7717 13.5741L12.9005 14.0649L15.8244 19.2546L16.6956 18.7637Z"
            mask="url(#path-1-inside-1)"
          />
        </StudentIconStyled>
      </IconWrapper>
    );
  },
);

const StudentIconStyled = styled('svg')<{
  $background: AllColorType | null;
  $borderColor: AllColorType | null;
}>`
  [data-id='${ICON_STUDENT_BACKGROUND}'] {
    fill: ${(props) => (props.$background ? `${props.theme.colors[props.$background]}` : 'transparent')};
  }
  [data-id='${ICON_STUDENT_BORDER}'] {
    fill: ${(props) => (props.$borderColor ? `${props.theme.colors[props.$borderColor]}` : 'transparent')};
  }
`;

StudentIcon.displayName = 'StudentIcon';

export default StudentIcon;
